import { createListenerMiddleware, type PayloadAction } from '@reduxjs/toolkit';

import { getPatternCandlesThunk } from '@/entities/pattern-candles';

import { logError } from '@/shared/lib/logging';

import {
    setLoadingCandles,
    setPatternCandles,
    setStudiedPatterns,
} from '../slices/pattern-lab.slice';
import type { PatternStudy } from '../types/pattern-study';

/**
 * Middleware that automatically loads candle data when new patterns are studied
 */
export const patternCandlesListener = createListenerMiddleware();

patternCandlesListener.startListening({
    actionCreator: setStudiedPatterns,
    effect: async (action: PayloadAction<PatternStudy[]>, listenerApi) => {
        const {
            patternLab: { loadedCandlesCache },
        } = listenerApi.getState() as {
            patternLab: { loadedCandlesCache: string[] };
        };

        // Filter patterns that don't have candles and are not in the cache
        const patternsToLoad = action.payload.filter(
            (pattern) =>
                !pattern.candles &&
                !loadedCandlesCache.includes(pattern.pattern.unique_id),
        );

        if (patternsToLoad.length === 0) {
            return;
        }

        listenerApi.dispatch(setLoadingCandles(true));

        try {
            // Load candles for each pattern
            await Promise.all(
                patternsToLoad.map(async (pattern) => {
                    try {
                        const parsedIndexList = JSON.parse(
                            pattern.pattern.pattern_index_list || '[]',
                        );

                        const candlesResult = await listenerApi.dispatch(
                            getPatternCandlesThunk({
                                master_idx: pattern.pattern.pattern_idx || 0,
                                subpattern_idx_list: parsedIndexList,
                                // eslint-disable-next-line no-underscore-dangle
                                pattern_len: pattern.pattern.pattern_len_ || 0,
                                timeframe: pattern.pattern.Timeframe,
                                ticker: pattern.pattern.Ticker || '',
                            }),
                        );

                        if (candlesResult.data) {
                            listenerApi.dispatch(
                                setPatternCandles({
                                    patternId: pattern.pattern.unique_id,
                                    candles: candlesResult.data,
                                    timeframe: pattern.pattern.Timeframe,
                                }),
                            );
                        }
                    } catch (e) {
                        logError(
                            `Failed to load candles for pattern ${pattern.pattern.unique_id}`,
                            e,
                        );
                    }
                }),
            );
        } catch (e) {
            logError(
                '[PatternCandlesListener middleware]: Failed to load candles',
                e,
            );
        } finally {
            listenerApi.dispatch(setLoadingCandles(false));
        }
    },
});
